import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';

// Trigger + monitor ingestion runs from the Admin page. Backed by /api/ingest/* (auth-gated).
type IngestRun = {
  id: string;
  province?: string | null;
  trigger: string;
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  finishedAt?: string | null;
  pagesFound: number;
  pagesProcessed: number;
  pagesRelevant: number;
  errors: number;
  costEstimate?: number | null;
};

const PROVINCES = [
  'British Columbia', 'Alberta', 'Saskatchewan', 'Manitoba', 'Ontario', 'Quebec',
  'New Brunswick', 'Nova Scotia', 'Prince Edward Island', 'Newfoundland and Labrador',
  'Yukon', 'Northwest Territories', 'Nunavut',
];

const inputCls =
  'bg-[var(--surface)] border border-border rounded-[10px] px-3 py-2 text-[14px] text-[color:var(--text-primary)] outline-none focus:border-[var(--accent)]';
const btnPrimary =
  'px-4 py-2 rounded-[10px] text-[14px] font-medium cursor-pointer transition-colors bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed';

const STATUS_COLOR: Record<string, string> = {
  running: 'var(--accent)', completed: '#34d399', failed: 'var(--danger-strong)',
};

const IngestRunMonitor: React.FC = () => {
  const [province, setProvince] = useState('British Columbia');
  const [runs, setRuns] = useState<IngestRun[]>([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try { const r = await axios.get('/api/ingest/runs?limit=15'); setRuns(r.data); }
    catch { /* not logged in / transient */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while any run is in progress.
  useEffect(() => {
    const anyRunning = runs.some((r) => r.status === 'running');
    if (anyRunning && !pollRef.current) {
      pollRef.current = setInterval(load, 5000);
    } else if (!anyRunning && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [runs, load]);

  const runNow = async () => {
    if (!window.confirm(`Start a crawl of ALL enabled URLs for ${province}? This can take a while and incurs API cost.`)) return;
    setBusy(true); setStatus(null);
    try {
      const r = await axios.post('/api/ingest/run', { province });
      setStatus(`Started run ${r.data.runId.slice(0, 8)}…`);
      await load();
    } catch (e: any) {
      setStatus(e?.response?.status === 409 ? 'A run is already in progress.' : 'Failed to start run.');
    } finally { setBusy(false); }
  };

  const dur = (r: IngestRun) => {
    if (!r.finishedAt) return '—';
    const ms = new Date(r.finishedAt).getTime() - new Date(r.startedAt).getTime();
    return `${Math.round(ms / 60000)}m`;
  };

  return (
    <div>
      <p className="text-[13px] text-[color:var(--text-secondary)] mb-4">
        Trigger a crawl of a province's enabled URLs, or watch scheduled/manual runs. One run at a time.
      </p>

      <div className="flex items-center gap-3 mb-6">
        <select value={province} onChange={(e) => setProvince(e.target.value)} className={inputCls + ' min-w-[220px]'}>
          {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <button type="button" onClick={runNow} disabled={busy} className={btnPrimary}>
          {busy ? 'Starting…' : 'Run crawl now'}
        </button>
        {status && <span className="text-[13px] text-[color:var(--text-secondary)]">{status}</span>}
      </div>

      {runs.length === 0 ? (
        <p className="text-[color:var(--text-muted)]">No runs yet.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {runs.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-[10px] bg-card border border-border px-3 py-2 text-[13px]">
              <span className="font-semibold uppercase text-[11px] w-[80px]" style={{ color: STATUS_COLOR[r.status] || 'var(--text-secondary)' }}>{r.status}</span>
              <span className="w-[150px] truncate text-[color:var(--text-primary)]">{r.province || '—'}</span>
              <span className="w-[60px] text-[color:var(--text-muted)]">{r.trigger}</span>
              <span className="flex-1 text-[color:var(--text-secondary)]">
                {r.pagesRelevant}/{r.pagesProcessed} relevant · {r.pagesFound} found{r.errors ? ` · ${r.errors} err` : ''}
              </span>
              <span className="w-[60px] text-[color:var(--text-muted)]">{dur(r)}</span>
              <span className="w-[64px] text-right text-[color:var(--text-muted)]">{r.costEstimate != null ? `$${Number(r.costEstimate).toFixed(2)}` : '—'}</span>
              <span className="w-[150px] text-right text-[color:var(--text-muted)]">{new Date(r.startedAt).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' })}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IngestRunMonitor;
